"use client";

import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Copy, Plus, Check, RotateCcw, ArrowLeft } from "lucide-react";
import { cn } from "@/lib/utils";
import { KakaoMockChat, ChatMessage } from "./KakaoMockChat";
import { toast } from "sonner";

interface SampleProduct {
    id: number;
    name: string;
    price: number;
}

const SAMPLE_PRODUCTS: SampleProduct[] = [
    { id: 1, name: "시카 수분크림", price: 28000 },
    { id: 2, name: "클렌징폼", price: 15000 },
    { id: 3, name: "선크림", price: 32000 },
    { id: 4, name: "토너", price: 18000 },
];

const INITIAL_MESSAGES: ChatMessage[] = [
    {
        id: "received-1",
        type: "received",
        content: "시카 수분크림이랑 클렌징폼 하나씩이요",
        time: "13:05",
    },
];

type DemoView = 'selection' | 'chat';

export function SettlementDemo() {
    const [selectedProducts, setSelectedProducts] = useState<{ product: SampleProduct; quantity: number }[]>([]);
    const [isCopied, setIsCopied] = useState(false);
    const [isPasted, setIsPasted] = useState(false);
    const [currentView, setCurrentView] = useState<DemoView>('selection');

    // 정산서 메시지 생성
    const settlementMessage = useMemo(() => {
        if (selectedProducts.length === 0) return null;

        const itemsStr = selectedProducts
            .map((item) => `${item.product.name} ${item.quantity}개 ${(item.product.price * item.quantity).toLocaleString()}원`)
            .join("\n");

        const totalProductPrice = selectedProducts.reduce((sum, item) => sum + item.product.price * item.quantity, 0);
        const FREE_SHIPPING_THRESHOLD = 30000;
        const shippingFee = totalProductPrice >= FREE_SHIPPING_THRESHOLD ? 0 : 3000;
        const totalPrice = totalProductPrice + shippingFee;

        const shippingNote = totalProductPrice >= FREE_SHIPPING_THRESHOLD ? "(무료배송 적용)" : "(무배 기준 3만원)";

        return `안녕하세요 고객님!
주문 내역 안내드립니다.

${itemsStr}
───────────────────
📦 상품 금액: ${totalProductPrice.toLocaleString()}원
🚚 배송비: ${shippingFee.toLocaleString()}원 ${shippingFee === 0 ? '' : shippingNote}
💰 입금액: ${totalPrice.toLocaleString()}원
💳 입금계좌: 123-456789 (카카오뱅크 ㄱㅌㅇ)`;
    }, [selectedProducts]);

    const addProduct = (product: SampleProduct) => {
        setSelectedProducts((prev) => {
            const existing = prev.find((p) => p.product.id === product.id);
            if (existing) {
                return prev.map((p) => (p.product.id === product.id ? { ...p, quantity: p.quantity + 1 } : p));
            } else {
                return [...prev, { product, quantity: 1 }];
            }
        });
        setIsCopied(false);
        setIsPasted(false);
    };

    const handleCopy = () => {
        if (!settlementMessage) return;
        navigator.clipboard.writeText(settlementMessage);
        setIsCopied(true);
        setCurrentView('chat'); // 복사 후 채팅 화면으로 전환
        toast.success("정산서가 복사되었습니다! 붙여넣기 버튼을 눌러보세요.");
    };

    const handlePaste = () => {
        setIsPasted(true);
        toast.success("전송 완료! 🎉");
    };

    const handleReset = () => {
        setSelectedProducts([]);
        setIsCopied(false);
        setIsPasted(false);
        setCurrentView('selection');
    };

    // 채팅 화면 (복사 후 표시)
    if (currentView === 'chat') {
        return (
            <div className="flex flex-col gap-4 w-full max-w-5xl mx-auto p-4 md:p-8 bg-gray-50 rounded-2xl border border-gray-100 shadow-sm">
                {!isPasted && (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setCurrentView('selection')}
                        className="self-start text-gray-500 hover:text-gray-700"
                    >
                        <ArrowLeft className="w-4 h-4 mr-1" />
                        상품 선택으로 돌아가기
                    </Button>
                )}

                <div className="text-center mb-2">
                    <h3 className="text-xl font-bold text-gray-800">
                        {isPasted ? "🎉 전송 완료!" : "📋 정산서가 복사되었어요!"}
                    </h3>
                    <p className="text-gray-600 text-sm mt-1">
                        {isPasted
                            ? "실제 서비스에서도 이렇게 간편하게 정산서를 보낼 수 있습니다."
                            : "아래 채팅창에서 '붙여넣기' 버튼을 눌러보세요!"}
                    </p>
                </div>

                <div className="h-[500px] w-full max-w-md mx-auto">
                    <KakaoMockChat
                        customerName="손님"
                        initialMessages={INITIAL_MESSAGES}
                        previewMessage={settlementMessage}
                        isPasted={isPasted}
                        onPaste={handlePaste}
                        className="h-full"
                        showPreview={false}
                    />
                </div>

                {isPasted && (
                    <div className="flex flex-col items-center gap-3 mt-4">
                        <Button
                            size="lg"
                            onClick={handleReset}
                            className="bg-brand-primary hover:bg-brand-primary/90 text-white font-bold gap-2"
                        >
                            <RotateCcw className="w-5 h-5" />
                            다시 해보기
                        </Button>
                        <p className="text-xs text-gray-500">다른 상품 조합으로 시도해보세요!</p>
                    </div>
                )}
            </div>
        );
    }

    // 상품 선택 화면 (기본)
    return (
        <div className="flex flex-col lg:flex-row gap-8 lg:items-start w-full max-w-5xl mx-auto p-4 md:p-8 bg-gray-50 rounded-2xl border border-gray-100 shadow-sm">
            {/* 왼쪽: 상품 선택 및 정산서 생성 영역 */}
            <div className="flex-1 flex flex-col gap-6">
                <div className="text-center lg:text-left">
                    <h3 className="text-2xl font-bold text-gray-800 mb-2">정산서 체험하기</h3>
                    <p className="text-gray-600 leading-relaxed text-sm md:text-base">
                        상품을 클릭하면 정산서가 자동으로 만들어집니다. 실제 사용 환경과 똑같이 체험해보세요!<br />
                        <span className="text-brand-primary font-medium text-xs md:text-sm">
                            * 회원가입 후 배송비, 입금계좌, 무료배송 기준금액을 자유롭게 설정할 수 있습니다.
                        </span>
                    </p>
                </div>

                {/* 상품 목록 */}
                <div className="grid grid-cols-2 gap-3">
                    {SAMPLE_PRODUCTS.map((product) => {
                        const selected = selectedProducts.find((p) => p.product.id === product.id);
                        return (
                            <button
                                key={product.id}
                                onClick={() => addProduct(product)}
                                className={cn(
                                    "relative flex flex-col items-start p-4 rounded-xl border transition-all text-left",
                                    selected
                                        ? "border-brand-primary bg-brand-primary/5 ring-2 ring-brand-primary/20"
                                        : "border-gray-200 bg-white hover:border-brand-primary/50 hover:shadow-md"
                                )}
                            >
                                <span className="font-semibold text-gray-800">{product.name}</span>
                                <span className="text-sm text-gray-500">{product.price.toLocaleString()}원</span>
                                {selected && (
                                    <div className="absolute top-2 right-2 flex items-center justify-center w-6 h-6 bg-brand-primary text-white rounded-full text-xs font-bold">
                                        {selected.quantity}
                                    </div>
                                )}
                            </button>
                        );
                    })}
                </div>

                {/* 선택된 상품 요약 & 정산서 미리보기 & 복사 버튼 */}
                <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm flex flex-col gap-4">
                    <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                        <h4 className="font-semibold text-gray-700">생성된 정산서</h4>
                        {selectedProducts.length > 0 && (
                            <span className="text-sm text-brand-primary font-medium">
                                총 {selectedProducts.reduce((acc, curr) => acc + curr.quantity, 0)}개
                            </span>
                        )}
                    </div>

                    {/* 정산서 텍스트 미리보기 영역 */}
                    <div className="bg-gray-50 rounded-lg p-4 min-h-[150px] relative border border-gray-200">
                        {settlementMessage ? (
                            <pre className="whitespace-pre-wrap text-sm text-gray-700 font-sans leading-relaxed">
                                {settlementMessage}
                            </pre>
                        ) : (
                            <div className="flex flex-col items-center justify-center h-full text-gray-400 text-sm gap-2 py-6">
                                <Plus className="w-8 h-8 opacity-20" />
                                <p>상품을 담으면 정산서가<br />여기에 나타납니다</p>
                            </div>
                        )}
                    </div>

                    <div className="pt-2">
                        <div className="flex justify-between items-center mb-4">
                            <div className="flex flex-col">
                                <span className="text-gray-600">총 결제금액 (배송비 포함)</span>
                                <span className="text-xs text-brand-primary font-medium">(무배 기준 3만원)</span>
                            </div>
                            <span className="text-xl font-bold text-brand-primary">
                                {settlementMessage
                                    ? (() => {
                                        const totalProductPrice = selectedProducts.reduce((sum, item) => sum + item.product.price * item.quantity, 0);
                                        const shippingFee = totalProductPrice >= 30000 ? 0 : 3000;
                                        return (totalProductPrice + shippingFee).toLocaleString();
                                    })()
                                    : "0"}원
                            </span>
                        </div>

                        <div className="flex flex-col gap-2">
                            <Button
                                size="lg"
                                className={cn(
                                    "w-full text-lg font-bold gap-2 transition-all",
                                    isCopied
                                        ? "bg-green-500 hover:bg-green-600 text-white"
                                        : "bg-brand-primary hover:bg-brand-primary/90"
                                )}
                                disabled={selectedProducts.length === 0}
                                onClick={handleCopy}
                            >
                                {isCopied ? (
                                    <>
                                        <Check className="w-5 h-5" />
                                        복사 완료! 채팅창에 붙여넣기 하세요
                                    </>
                                ) : (
                                    <>
                                        <Copy className="w-5 h-5" />
                                        정산서 복사하기 (클립보드)
                                    </>
                                )}
                            </Button>

                            {/* 초기화 버튼 */}
                            {selectedProducts.length > 0 && (
                                <Button
                                    variant="outline"
                                    onClick={handleReset}
                                    className="w-full text-gray-500 hover:text-red-500 hover:bg-red-50 border-gray-200"
                                >
                                    초기화하기
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* 오른쪽: 카톡 모의 채팅 - lg 이상에서만 표시 (힌트 역할) */}
            <div className="hidden lg:flex flex-1 h-[650px] flex-col w-full">
                <KakaoMockChat
                    customerName="손님"
                    initialMessages={INITIAL_MESSAGES}
                    previewMessage={settlementMessage}
                    isPasted={isPasted}
                    onPaste={handlePaste}
                    className="flex-1"
                    showPreview={false}
                />
            </div>
        </div>
    );
}

